<template>
  <a-input
    v-model:value="displayValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :size="size"
    :allow-clear="allowClear"
    :maxlength="maxLength"
    :status="computedStatus"
    class="ant-bank-account-input"
    @update:value="onInput"
    @blur="onBlur"
    @pressEnter="onPressEnter"
  >
    <template #prefix>
      <i class="fas fa-piggy-bank" />
    </template>
  </a-input>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";

interface Props {
  modelValue?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  status?: "" | "error" | "warning";
  minLength?: number;
  maxLength?: number;
  /** Group digits: #### #### #### */
  grouped?: boolean;
  validateOnBlur?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  placeholder: "Nhập số tài khoản ngân hàng",
  disabled: false,
  size: "middle",
  allowClear: true,
  status: "",
  minLength: 6,
  maxLength: 20,
  grouped: true,
  validateOnBlur: true,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "change", value: string): void;
  (e: "blur", event: FocusEvent): void;
  (e: "pressEnter", event: KeyboardEvent): void;
  (e: "validate", valid: boolean): void;
}>();

const displayValue = ref("");

const onlyDigits = (value: string) =>
  value.replace(/\D/g, "").slice(0, props.maxLength);

const formatGrouped = (digits: string) =>
  props.grouped ? digits.replace(/(.{4})/g, "$1 ").trim() : digits;

const isValid = (digits: string) => {
  if (!digits) return true;
  return digits.length >= props.minLength && digits.length <= props.maxLength;
};

watch(
  () => props.modelValue,
  (val) => {
    displayValue.value = formatGrouped(onlyDigits(val ?? ""));
  },
  { immediate: true }
);

const localError = computed(() => {
  if (!props.validateOnBlur) return false;
  const digits = onlyDigits(props.modelValue ?? "");
  return !!digits && !isValid(digits);
});

const computedStatus = computed(() => props.status || (localError.value ? "error" : ""));

const onInput = (val: string) => {
  const digits = onlyDigits(val);
  displayValue.value = formatGrouped(digits);
  emit("update:modelValue", digits);
  emit("change", digits);
};

const onBlur = (e: FocusEvent) => {
  emit("validate", isValid(onlyDigits(props.modelValue ?? "")));
  emit("blur", e);
};

const onPressEnter = (e: KeyboardEvent) => emit("pressEnter", e);
</script>

<!--
  Hướng dẫn sử dụng — Bank Account
  Global: <h-bankAccount v-model="account" :min-length="6" :max-length="20" />
  Import: import { BankAccountInput } from '@/components/ant/input'

  Props: modelValue, minLength, maxLength, grouped, validateOnBlur...
  Events: update:modelValue, change, blur, pressEnter, validate
  Docs: src/components/ant/input/README.md
-->
